/**
 * Lightweight RFC 5545 RRULE Parser
 * Implements a subset of iCalendar recurrence rules for calendar event scheduling
 *
 * Supported features:
 * - FREQ: DAILY, WEEKLY, MONTHLY
 * - INTERVAL: Recurrence interval (e.g., every 2 weeks)
 * - COUNT: Number of occurrences
 * - UNTIL: End date for recurrence
 * - BYDAY: Day of week for WEEKLY frequency (MO, TU, WE, TH, FR, SA, SU)
 *
 * @see RFC 5545 Section 3.3.10 - Recurrence Rule
 */

/**
 * Frequency of recurrence
 */
export enum Frequency {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

/**
 * Days of the week (RFC 5545 format)
 */
export enum Weekday {
  SU = 'SU',
  MO = 'MO',
  TU = 'TU',
  WE = 'WE',
  TH = 'TH',
  FR = 'FR',
  SA = 'SA',
}

/**
 * Mapping weekday abbreviations to numeric values (Sunday = 0)
 */
export const WEEKDAY_TO_NUMBER: Record<Weekday, number> = {
  [Weekday.SU]: 0,
  [Weekday.MO]: 1,
  [Weekday.TU]: 2,
  [Weekday.WE]: 3,
  [Weekday.TH]: 4,
  [Weekday.FR]: 5,
  [Weekday.SA]: 6,
}

/**
 * Parsed RRULE structure
 */
export interface RRule {
  /** Frequency of recurrence */
  freq: Frequency
  /** Interval between recurrences (default: 1) */
  interval: number
  /** Maximum number of occurrences (mutually exclusive with until) */
  count?: number
  /** End date for recurrence (mutually exclusive with count) */
  until?: Date
  /** Days of week for WEEKLY frequency */
  byDay?: Weekday[]
}

/**
 * Parses an RRULE string into structured data
 *
 * @param rruleString - RFC 5545 RRULE string (e.g., "FREQ=WEEKLY;INTERVAL=2;COUNT=10;BYDAY=MO,WE,FR")
 * @returns Parsed RRule object
 * @throws {Error} If RRULE string is invalid or contains unsupported features
 *
 * @example
 * ```typescript
 * // Every week on Monday and Wednesday, 10 times
 * const rule = parseRRule('FREQ=WEEKLY;BYDAY=MO,WE;COUNT=10')
 *
 * // Every 2 days until December 31, 2025
 * const rule2 = parseRRule('FREQ=DAILY;INTERVAL=2;UNTIL=20251231T235959Z')
 *
 * // Monthly on the same day, ending on a specific date
 * const rule3 = parseRRule('FREQ=MONTHLY;UNTIL=20251231')
 * ```
 */
export function parseRRule(rruleString: string): RRule {
  if (!rruleString || typeof rruleString !== 'string') {
    throw new Error('RRULE string is required')
  }

  // Remove "RRULE:" prefix if present
  const cleanedString = rruleString.replace(/^RRULE:/i, '').trim()

  if (!cleanedString) {
    throw new Error('RRULE string cannot be empty')
  }

  // Parse key-value pairs
  const parts = cleanedString.split(';')
  const params: Record<string, string> = {}

  for (const part of parts) {
    const [key, value] = part.split('=')
    if (!key || !value) {
      throw new Error(`Invalid RRULE parameter: ${part}`)
    }
    params[key.toUpperCase()] = value
  }

  // Validate FREQ (required)
  if (!params.FREQ) {
    throw new Error('FREQ is required in RRULE')
  }

  const freq = params.FREQ.toUpperCase() as Frequency
  if (!Object.values(Frequency).includes(freq)) {
    throw new Error(
      `Unsupported FREQ value: ${params.FREQ}. Supported values: DAILY, WEEKLY, MONTHLY`
    )
  }

  // Parse INTERVAL (default: 1)
  const interval = params.INTERVAL ? parseInt(params.INTERVAL, 10) : 1
  if (isNaN(interval) || interval < 1) {
    throw new Error(`Invalid INTERVAL value: ${params.INTERVAL}. Must be a positive integer.`)
  }

  // Parse COUNT (optional)
  let count: number | undefined
  if (params.COUNT) {
    count = parseInt(params.COUNT, 10)
    if (isNaN(count) || count < 1) {
      throw new Error(`Invalid COUNT value: ${params.COUNT}. Must be a positive integer.`)
    }
  }

  // Parse UNTIL (optional, mutually exclusive with COUNT)
  let until: Date | undefined
  if (params.UNTIL) {
    if (count) {
      throw new Error('COUNT and UNTIL are mutually exclusive')
    }

    // Parse UNTIL date (supports both DATE and DATE-TIME formats)
    // Format: YYYYMMDD or YYYYMMDDTHHMMSSZ
    until = parseUntilDate(params.UNTIL)
  }

  // Parse BYDAY (optional, only for WEEKLY frequency)
  let byDay: Weekday[] | undefined
  if (params.BYDAY) {
    if (freq !== Frequency.WEEKLY) {
      throw new Error('BYDAY is only supported for WEEKLY frequency')
    }

    const days = params.BYDAY.split(',').map((d) => d.trim().toUpperCase())
    byDay = []

    for (const day of days) {
      if (!Object.values(Weekday).includes(day as Weekday)) {
        throw new Error(
          `Invalid BYDAY value: ${day}. Supported values: SU, MO, TU, WE, TH, FR, SA`
        )
      }
      byDay.push(day as Weekday)
    }

    if (byDay.length === 0) {
      throw new Error('BYDAY must contain at least one weekday')
    }
  }

  return {
    freq,
    interval,
    count,
    until,
    byDay,
  }
}

/**
 * Parses UNTIL date from RRULE format
 * Supports both DATE (YYYYMMDD) and DATE-TIME (YYYYMMDDTHHMMSSZ) formats
 *
 * @param untilString - UNTIL value from RRULE
 * @returns Date object
 * @throws {Error} If date format is invalid
 */
function parseUntilDate(untilString: string): Date {
  // Remove 'Z' suffix if present
  const cleaned = untilString.replace(/Z$/i, '')

  // Check for DATE-TIME format (YYYYMMDDTHHmmss)
  const dateTimeMatch = cleaned.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})$/)
  if (dateTimeMatch) {
    const [, year, month, day, hour, minute, second] = dateTimeMatch
    const date = new Date(
      parseInt(year, 10),
      parseInt(month, 10) - 1, // Month is 0-indexed
      parseInt(day, 10),
      parseInt(hour, 10),
      parseInt(minute, 10),
      parseInt(second, 10)
    )

    if (isNaN(date.getTime())) {
      throw new Error(`Invalid UNTIL date: ${untilString}`)
    }

    return date
  }

  // Check for DATE format (YYYYMMDD)
  const dateMatch = cleaned.match(/^(\d{4})(\d{2})(\d{2})$/)
  if (dateMatch) {
    const [, year, month, day] = dateMatch
    const date = new Date(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10),
      23,
      59,
      59,
      999
    )

    if (isNaN(date.getTime())) {
      throw new Error(`Invalid UNTIL date: ${untilString}`)
    }

    return date
  }

  throw new Error(
    `Invalid UNTIL format: ${untilString}. Expected YYYYMMDD or YYYYMMDDTHHMMSSZ`
  )
}

/**
 * Serializes an RRule object back to RRULE string format
 *
 * @param rule - RRule object
 * @returns RRULE string (without "RRULE:" prefix)
 *
 * @example
 * ```typescript
 * const rule: RRule = {
 *   freq: Frequency.WEEKLY,
 *   interval: 2,
 *   count: 10,
 *   byDay: [Weekday.MO, Weekday.WE]
 * }
 * const rruleString = serializeRRule(rule)
 * // => "FREQ=WEEKLY;INTERVAL=2;COUNT=10;BYDAY=MO,WE"
 * ```
 */
export function serializeRRule(rule: RRule): string {
  const parts: string[] = []

  // FREQ (required)
  parts.push(`FREQ=${rule.freq}`)

  // INTERVAL (only if not default)
  if (rule.interval && rule.interval !== 1) {
    parts.push(`INTERVAL=${rule.interval}`)
  }

  // COUNT or UNTIL (mutually exclusive)
  if (rule.count) {
    parts.push(`COUNT=${rule.count}`)
  } else if (rule.until) {
    const untilString = formatUntilDate(rule.until)
    parts.push(`UNTIL=${untilString}`)
  }

  // BYDAY (only for WEEKLY)
  if (rule.byDay && rule.byDay.length > 0) {
    parts.push(`BYDAY=${rule.byDay.join(',')}`)
  }

  return parts.join(';')
}

/**
 * Formats a Date object to RRULE UNTIL format (YYYYMMDDTHHMMSSZ)
 *
 * @param date - Date to format
 * @returns UNTIL date string
 */
function formatUntilDate(date: Date): string {
  const year = date.getFullYear().toString().padStart(4, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  const hour = date.getHours().toString().padStart(2, '0')
  const minute = date.getMinutes().toString().padStart(2, '0')
  const second = date.getSeconds().toString().padStart(2, '0')

  return `${year}${month}${day}T${hour}${minute}${second}Z`
}

/**
 * Validates an RRULE string without fully parsing it
 *
 * @param rruleString - RRULE string to validate
 * @returns true if valid, false otherwise
 */
export function isValidRRule(rruleString: string): boolean {
  try {
    parseRRule(rruleString)
    return true
  } catch {
    return false
  }
}

/**
 * Generates a human-readable description of an RRULE
 *
 * @param rule - RRule object or RRULE string
 * @returns Human-readable description
 *
 * @example
 * ```typescript
 * const description = describeRRule('FREQ=WEEKLY;INTERVAL=2;BYDAY=MO,WE;COUNT=10')
 * // => "Every 2 weeks on Monday, Wednesday, 10 times"
 * ```
 */
export function describeRRule(rule: RRule | string): string {
  const parsed = typeof rule === 'string' ? parseRRule(rule) : rule

  let description = ''

  // Interval prefix
  if (parsed.interval === 1) {
    description = 'Every '
  } else {
    description = `Every ${parsed.interval} `
  }

  // Frequency
  switch (parsed.freq) {
    case Frequency.DAILY:
      description += parsed.interval === 1 ? 'day' : 'days'
      break
    case Frequency.WEEKLY:
      description += parsed.interval === 1 ? 'week' : 'weeks'
      break
    case Frequency.MONTHLY:
      description += parsed.interval === 1 ? 'month' : 'months'
      break
  }

  // BYDAY for weekly
  if (parsed.byDay && parsed.byDay.length > 0) {
    const dayNames = parsed.byDay.map((d) => formatWeekday(d))
    description += ` on ${dayNames.join(', ')}`
  }

  // End condition
  if (parsed.count) {
    description += `, ${parsed.count} times`
  } else if (parsed.until) {
    const dateStr = parsed.until.toLocaleDateString()
    description += `, until ${dateStr}`
  }

  return description
}

/**
 * Formats a weekday abbreviation to full name
 */
function formatWeekday(day: Weekday): string {
  const names: Record<Weekday, string> = {
    [Weekday.SU]: 'Sunday',
    [Weekday.MO]: 'Monday',
    [Weekday.TU]: 'Tuesday',
    [Weekday.WE]: 'Wednesday',
    [Weekday.TH]: 'Thursday',
    [Weekday.FR]: 'Friday',
    [Weekday.SA]: 'Saturday',
  }
  return names[day]
}
